import { PartyTypes } from '../enums';
import { keepsBookedSlot } from './keeps-booked-slot';

export interface PartyDisplayWindowInput {
  type?: PartyTypes;
  quoted_time: number;
  seated_time?: number | null;
  duration?: number | null;
}

export interface PartyDisplayWindow {
  start: number;
  end: number;
}

/**
 * Planned service window for rendering a party bar on schedule views (day grid / timeline).
 *
 * - Reservations start at min(quoted_time, seated_time) and end at the booked end,
 *   quoted_time + duration: an early seat starts the bar earlier, a late seat keeps the end.
 *   A reservation seated after its slot ended renders from its seated time, like a walk-in.
 * - Walk-ins / waitlist / to-go anchor at seated_time once seated — quoted_time is only the
 *   join/quote time, so a walk-in quoted at 10:00 but seated at 11:30 renders 11:30 onward.
 *
 * Companion to getPartyOccupancy (the conflict-guard truth): same seated-anchor semantics for
 * non-reservations, but display never abstains (no servingTracking gate, no null) and never
 * extends through "now" — a bar is a plan, not a live occupancy claim. `fallbackDuration`
 * sizes bars for parties without a recorded duration (callers pass the max enabled service
 * duration, mirroring the availability engine's default seated duration).
 */
export function getPartyDisplayWindow(
  party: PartyDisplayWindowInput,
  fallbackDuration: number,
): PartyDisplayWindow {
  const duration = party.duration || fallbackDuration;
  const seated = party.seated_time ?? null;

  if (seated != null && !keepsBookedSlot(party, duration)) {
    return { start: seated, end: seated + duration };
  }

  const start = seated != null ? Math.min(party.quoted_time, seated) : party.quoted_time;

  return { start, end: party.quoted_time + duration };
}
